// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { SwiperInstance } from "@eisinc/App/Components/XngCarousel/models";
import { xClassNames } from "@eisinc/XDL/Utilities";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useState } from "react";

interface ArrowElementProps {
    swiper: SwiperInstance | null;
    arrowType: string;
    showOnMobile?: boolean;
    hideArrowIfDisabled?: boolean;
}

interface State {
    className?: string;
    disabled?: boolean;
    element?: HTMLElement;
}

export function useArrowElement({ swiper, arrowType, showOnMobile }: ArrowElementProps) {
    const [options, setOptions] = useState<State>({
        className: "",
        element: undefined,
        disabled: true,
    });

    const debouncedUpdateArrowOptions = useCallback(
        debounce((swiper: SwiperInstance) => {
            updateArrowOptions(swiper);
        }, 200),
        [],
    );

    const updateArrowOptions = (swiper: SwiperInstance) => {
        const arrowElement = arrowType === "next" ? swiper?.navigation?.nextEl : swiper?.navigation?.prevEl;
        if (!arrowElement) {
            return;
        }

        const isDisabled = arrowElement.className.indexOf("swiper-button-disabled") > -1;

        const classNames = xClassNames.append([
            `arrow-button xng-carousel-${arrowType}`,
            showOnMobile ? "arrow-on-mobile" : "arrow-on-desktop",
            arrowElement.className.includes("swiper-button-lock") ? "swiper-button-lock" : "",
            isDisabled ? "swiper-button-disabled" : "",
        ]);

        setOptions({
            className: classNames,
            disabled: arrowElement.className.indexOf("swiper-button-disabled") > -1,
            element: arrowElement,
        });
    };

    useEffect(() => {
        if (!swiper) {
            return;
        }

        swiper.on("slideChange", debouncedUpdateArrowOptions);
        swiper.on("_slideClasses", debouncedUpdateArrowOptions);
        debouncedUpdateArrowOptions(swiper);

        return () => {
            swiper.off("slideChange", debouncedUpdateArrowOptions);
            swiper.off("_slideClasses", debouncedUpdateArrowOptions);
        };
    }, []);

    return options;
}

export function useSwiperHooks(swiper: SwiperInstance | null) {
    const [isLocked, setIsLocked] = useState(true);
    useEffect(() => {
        const handler = (swiper: SwiperInstance) => {
            setIsLocked(swiper.isLocked);
        };
        swiper?.on("_slideClasses", handler);
        return () => {
            swiper?.off("_slideClasses", handler);
        };
    }, [swiper]);
    return {
        isLocked,
    };
}
